import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { api } from '../App';
import { ArrowLeft, Download, Sparkles, CheckCircle2, AlertCircle, Lightbulb, Target, Info, BookOpen, Briefcase, TrendingUp, Heart, Users, Award, Crown, Zap, Lock, Star } from 'lucide-react';

// Mizaç tipi tanımları
const TEMPERAMENT_TYPES = {
  1: {
    name: "Mükemmeliyetçi / Reformcu",
    description: "İlkeli, amaçlı ve kendini kontrol eden. Doğru olanı yapma ve her şeyi iyileştirme arzusu güçlüdür.",
    strengths: ["Sorumluluk sahibi", "Detaycı", "Organize", "İlkeli", "Güvenilir"],
    challenges: ["Mükemmeliyetçilik", "Eleştiriye hassaslık", "Esneklik zorluğu"]
  },
  2: {
    name: "Yardımsever / Destekleyici",
    description: "Sıcak, empatik ve cömert. İnsanlara yardım etmekten mutluluk duyar, ilişkilere önem verir.",
    strengths: ["Empatik", "Destekleyici", "İlişki kurmada başarılı", "Cömert", "Fedakar"],
    challenges: ["Sınır koyma zorluğu", "Kendi ihtiyaçlarını ihmal etme", "Onay arayışı"]
  },
  3: {
    name: "Başarılı / Motive Edici",
    description: "Uyumlu, başarı odaklı ve enerji dolu. Hedeflerine ulaşmada kararlı ve motive edicidir.",
    strengths: ["Başarı odaklı", "Motive edici", "Uyumlu", "Enerjik", "Pratik"],
    challenges: ["İmaj kaygısı", "İş-özel yaşam dengesi", "Duygularını göstermekte zorlanma"]
  },
  4: {
    name: "Bireysel / Yaratıcı",
    description: "Yaratıcı, hassas ve duygusal olarak derindir. Özgünlük ve anlam arayışındadır.",
    strengths: ["Yaratıcı", "Empatik", "Özgün", "Derin düşünen", "Sanatsal"],
    challenges: ["Duygusal yoğunluk", "Kendini farklı hissetme", "Melankoli eğilimi"]
  },
  5: {
    name: "Araştırmacı / Gözlemci",
    description: "Analitik, bağımsız ve bilgi edinmeye meraklı. Gözlem yaparak ve düşünerek öğrenir.",
    strengths: ["Analitik", "Objektif", "Bağımsız", "Meraklı", "Derin düşünen"],
    challenges: ["Sosyal durumlardan kaçınma", "Duygularını ifade etmekte zorlanma", "İzolasyon"]
  },
  6: {
    name: "Sadık / Güvenlik Arayan",
    description: "Bağlı, sorumlu ve güvenilir. Güvenlik ve istikrar arayışındadır.",
    strengths: ["Sadık", "Sorumlu", "Öngörülü", "Destekleyici", "Güvenilir"],
    challenges: ["Endişe ve kaygı", "Karar vermekte zorlanma", "Otoriteye karşı çelişkili duygular"]
  },
  7: {
    name: "Coşkulu / Maceracı",
    description: "Heyecanlı, çok yönlü ve iyimser. Yeni deneyimlere açık ve enerjiktir.",
    strengths: ["İyimser", "Enerjik", "Yaratıcı", "Esnek", "Hevesli"],
    challenges: ["Erteleme", "Yüzeysellik", "Sorumluluktan kaçma", "Konsantrasyon zorluğu"]
  },
  8: {
    name: "Lider / Koruyucu",
    description: "Güçlü, kararlı ve kendinden emin. Kontrol sahibi olmak ve adalet sağlamak ister.",
    strengths: ["Liderlik", "Kararlılık", "Koruyucu", "Güçlü", "Adil"],
    challenges: ["Kontrolcülük", "Saldırganlık", "Hassasiyet göstermekte zorlanma"]
  },
  9: {
    name: "Barışçıl / Uyumlu",
    description: "Kabul edici, güvenilir ve dengeli. Uyum ve huzur arayışındadır.",
    strengths: ["Uyumlu", "Sabırlı", "Destekleyici", "Dinleyici", "Sakin"],
    challenges: ["Pasiflik", "Erteleme", "Kendi görüşlerini ifade etmekte zorlanma", "Uyuşukluk"]
  }
};

export default function ReportView({ user }) {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [temperamentType, setTemperamentType] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [subscription, setSubscription] = useState(null);

  const isFreeTier = !subscription || subscription.plan?.tier === 'free' || subscription.subscription?.tier === 'free';
  const isIndividualUser = user?.role === 'individual';

  useEffect(() => {
    fetchReport();
    fetchSubscription();
  }, [reportId]);

  const fetchSubscription = async () => {
    try {
      const response = await api.get('/subscription/status');
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const fetchReport = async () => {
    try {
      const response = await api.get(`/reports/${reportId}`);
      const reportData = response.data;
      
      // Safety net: if content_json is a string, try to parse it
      if (reportData.content_json && typeof reportData.content_json === 'string') {
        try {
          let cleaned = reportData.content_json.trim();
          // Strip markdown code blocks
          cleaned = cleaned.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?```\s*$/, '');
          reportData.content_json = JSON.parse(cleaned);
        } catch (e) {
          // If still can't parse, wrap in a display-friendly structure
          reportData.content_json = {
            title: 'Mizaç Raporu',
            summary: reportData.content_json,
            content: reportData.content_json
          };
        }
      }
      
      setReport(reportData);
      
      // Extract temperament type from content
      const content = reportData.content_json;
      if (content && content.temperament_type) {
        setTemperamentType(content.temperament_type);
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Rapor yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  
  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/reports/${reportId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mizac_raporu_${reportId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      
      toast.success('PDF başarıyla indirildi');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('PDF indirme sırasında hata oluştu');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 flex items-center justify-center p-6">
        <Card className="max-w-md text-center">
          <CardContent className="pt-6">
            <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">Rapor bulunamadı</p>
            <Button onClick={() => navigate('/')}>Panele Dön</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const content = report.content_json;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Panele Dön
          </Button>
          <Button 
            variant="outline" 
            onClick={downloadPDF}
            disabled={downloading}
            data-testid="download-report-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading ? 'İndiriliyor...' : 'PDF İndir'}
          </Button>
        </div>
      </header>

      {/* Report Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 pb-20">
        <div className="animate-fade-in space-y-8">
          {/* Title Section */}
          <Card className="shadow-xl border-none bg-gradient-to-br from-blue-600 to-emerald-600 text-white" data-testid="report-header">
            <CardHeader className="text-center pb-8">
              <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-3xl font-bold">
                {content.title || 'Mizaç Raporu'}
              </CardTitle>
              <CardDescription className="text-blue-100 mt-2">
                Oluşturulma Tarihi: {new Date(report.created_at).toLocaleDateString('tr-TR')}
              </CardDescription>
            </CardHeader>
          </Card>

          {/* Upgrade Banner for Free Users */}
          {isIndividualUser && isFreeTier && (
            <Card className="shadow-xl border-2 border-amber-200 bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50" data-testid="upgrade-banner">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center">
                      <Crown className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        Premium'a Yükselt
                        <Badge className="bg-amber-100 text-amber-700">Önerilen</Badge>
                      </h3>
                      <p className="text-slate-600 text-sm">
                        Sınırsız rapor, PDF indirme ve AI Koç ile daha fazlasını keşfedin
                      </p>
                    </div>
                  </div>
                  <Button 
                    className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 px-8"
                    onClick={() => navigate('/upgrade')}
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Premium'a Geç
                  </Button>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-amber-200">
                  <div className="text-center">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <p className="text-sm font-medium text-slate-700">Sınırsız Rapor</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Download className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-sm font-medium text-slate-700">PDF İndirme</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Star className="w-5 h-5 text-purple-600" />
                    </div>
                    <p className="text-sm font-medium text-slate-700">AI Koç</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Temperament Type Card */}
          {temperamentType && TEMPERAMENT_TYPES[temperamentType] && (
            <Card className="shadow-lg border-none bg-gradient-to-br from-purple-50 to-blue-50" data-testid="temperament-type-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Info className="w-6 h-6 text-purple-600" />
                  Ana Mizaç Tipiniz: {TEMPERAMENT_TYPES[temperamentType].name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-700 leading-relaxed">
                  {TEMPERAMENT_TYPES[temperamentType].description}
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-white rounded-lg">
                    <h4 className="font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Tipik Güçlü Yönler
                    </h4>
                    <ul className="space-y-1 text-sm text-slate-600">
                      {TEMPERAMENT_TYPES[temperamentType].strengths.map((s, idx) => (
                        <li key={idx}>• {s}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-white rounded-lg">
                    <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Zorlanma Alanları
                    </h4>
                    <ul className="space-y-1 text-sm text-slate-600">
                      {TEMPERAMENT_TYPES[temperamentType].challenges.map((c, idx) => (
                        <li key={idx}>• {c}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Not:</strong> Mizaç tipleri kaderini belirlemez. Herkes zamanla gelişir ve değişir. Bu bilgiler seni daha iyi anlamana yardımcı olmak içindir.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Strengths */}
          {content.strengths && (
            <Card className="shadow-lg border-none" data-testid="strengths-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  Güçlü Yönler
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {content.strengths.map((strength, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{strength}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Challenges */}
          {(content.challenges || content.challenge_areas) && (
            <Card className="shadow-lg border-none" data-testid="challenges-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <AlertCircle className="w-6 h-6 text-blue-600" />
                  Zorlanabildiğin Durumlar
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {(content.challenges || content.challenge_areas).map((challenge, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{challenge}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Learning Style */}
          {content.learning_style && (
            <Card className="shadow-lg border-none" data-testid="learning-style-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Lightbulb className="w-6 h-6 text-yellow-600" />
                  Öğrenme Tarzın
                </CardTitle>
              </CardHeader>
              <CardContent>
                {typeof content.learning_style === 'string' ? (
                  <p className="text-slate-700 leading-relaxed">{content.learning_style}</p>
                ) : (
                  <div className="space-y-4">
                    {content.learning_style.in_class && (
                      <div className="p-4 bg-yellow-50 rounded-lg">
                        <h4 className="font-semibold text-yellow-800 mb-2">Derste:</h4>
                        <p className="text-slate-700 text-sm">{content.learning_style.in_class}</p>
                      </div>
                    )}
                    {content.learning_style.note_taking && (
                      <div className="p-4 bg-yellow-50 rounded-lg">
                        <h4 className="font-semibold text-yellow-800 mb-2">Not Alma:</h4>
                        <p className="text-slate-700 text-sm">{content.learning_style.note_taking}</p>
                      </div>
                    )}
                    {content.learning_style.exam_preparation && (
                      <div className="p-4 bg-yellow-50 rounded-lg">
                        <h4 className="font-semibold text-yellow-800 mb-2">Sınav Hazırlığı:</h4>
                        <p className="text-slate-700 text-sm">{content.learning_style.exam_preparation}</p>
                      </div>
                    )}
                    {content.learning_style.group_work && (
                      <div className="p-4 bg-yellow-50 rounded-lg">
                        <h4 className="font-semibold text-yellow-800 mb-2">Grup Çalışması:</h4>
                        <p className="text-slate-700 text-sm">{content.learning_style.group_work}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Emotional Management */}
          {/* Emotional Management - Array format */}
          {content.emotional_management && Array.isArray(content.emotional_management) && (
            <Card className="shadow-lg border-none" data-testid="emotional-management-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Target className="w-6 h-6 text-purple-600" />
                  Duygularını Yönetme Önerileri
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {content.emotional_management.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                      <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                        {idx + 1}
                      </div>
                      <span className="text-slate-700">{tip}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Emotion Regulation - Object format with signals and tips */}
          {content.emotion_regulation && (
            <Card className="shadow-lg border-none" data-testid="emotion-regulation-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Target className="w-6 h-6 text-purple-600" />
                  Duygu Düzenleme
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {content.emotion_regulation.signals && (
                  <div>
                    <h4 className="font-semibold text-purple-800 mb-2">Dikkat Edilmesi Gereken Sinyaller:</h4>
                    <ul className="space-y-2">
                      {content.emotion_regulation.signals.map((signal, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-700 text-sm">
                          <AlertCircle className="w-4 h-4 text-purple-600 flex-shrink-0 mt-1" />
                          <span>{signal}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {content.emotion_regulation.tips && (
                  <div>
                    <h4 className="font-semibold text-purple-800 mb-2">Öneriler:</h4>
                    <ul className="space-y-3">
                      {content.emotion_regulation.tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                          <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                            {idx + 1}
                          </div>
                          <span className="text-slate-700 text-sm">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Micro Habits */}
          {content.micro_habits && (
            <Card className="shadow-lg border-none bg-gradient-to-br from-green-50 to-emerald-50" data-testid="micro-habits-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Sparkles className="w-6 h-6 text-emerald-600" />
                  Mikro Alışkanlıklar
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {content.micro_habits.map((habit, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                      <div className="w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                        {idx + 1}
                      </div>
                      <span className="text-slate-700">{habit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Action Steps */}
          {content.action_steps && (
            <Card className="shadow-lg border-none bg-gradient-to-br from-blue-50 to-emerald-50" data-testid="action-steps-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Target className="w-6 h-6 text-blue-600" />
                  Bugünden İtibaren Deneyebileceğin 3 Küçük Adım
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {content.action_steps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-4 p-4 bg-white rounded-lg shadow-sm">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-emerald-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                        {idx + 1}
                      </div>
                      <p className="text-slate-700 font-medium pt-2">{step}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Parent-specific sections */}
          {content.parent_tips && (
            <Card className="shadow-lg border-none" data-testid="parent-tips-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Lightbulb className="w-6 h-6 text-blue-600" />
                  Ebeveyn Olarak Dikkat Etmeniz Gerekenler
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {content.parent_tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                      <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{tip}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {content.practical_suggestions && (
            <Card className="shadow-lg border-none" data-testid="practical-suggestions-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Target className="w-6 h-6 text-emerald-600" />
                  Evde Uygulayabileceğiniz Öneriler
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {content.practical_suggestions.map((suggestion, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                      <div className="w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                        {idx + 1}
                      </div>
                      <span className="text-slate-700">{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* School Strategies */}
          {content.school_strategies && (
            <Card className="shadow-lg border-none" data-testid="school-strategies-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <BookOpen className="w-6 h-6 text-indigo-600" />
                  Okul Hayatında Başarı Stratejileri
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {content.school_strategies.study_techniques && (
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-2">Ders Çalışma Teknikleri:</h4>
                    <ul className="space-y-2">
                      {content.school_strategies.study_techniques.map((tech, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-1" />
                          <span>{tech}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {content.school_strategies.exam_preparation && (
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-2">Sınav Hazırlığı:</h4>
                    <ul className="space-y-2">
                      {content.school_strategies.exam_preparation.map((prep, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-700">
                          <Target className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-1" />
                          <span>{prep}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {content.school_strategies.social_activities && (
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-2">Sosyal Aktiviteler:</h4>
                    <ul className="space-y-2">
                      {content.school_strategies.social_activities.map((activity, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-700">
                          <Sparkles className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-1" />
                          <span>{activity}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Career Suggestions */}
          {content.career_suggestions && (
            <Card className="shadow-lg border-none bg-gradient-to-br from-amber-50 to-orange-50" data-testid="career-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Briefcase className="w-6 h-6 text-orange-600" />
                  Kariyer ve Meslek Rehberi
                </CardTitle>
                <CardDescription>
                  Mizaç yapınıza uygun kariyer önerileri
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {content.career_suggestions.suitable_careers && (
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-3">Size Uygun Meslekler:</h4>
                    <div className="space-y-3">
                      {content.career_suggestions.suitable_careers.map((item, idx) => (
                        <div key={idx} className="p-3 bg-white rounded-lg border border-orange-200">
                          <p className="font-semibold text-orange-800">
                            {typeof item === 'object' ? item.career : item}
                          </p>
                          {typeof item === 'object' && item.reason && (
                            <p className="text-sm text-slate-600 mt-1">{item.reason}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {content.career_suggestions.skills_to_develop && (
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-2">Geliştirilmesi Önerilen Beceriler:</h4>
                    <div className="flex flex-wrap gap-2">
                      {content.career_suggestions.skills_to_develop.map((skill, idx) => (
                        <span key={idx} className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Workplace Strategies - Adult */}
          {content.workplace_strategies && (
            <Card className="shadow-lg border-none bg-gradient-to-br from-violet-50 to-purple-50" data-testid="workplace-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Briefcase className="w-6 h-6 text-purple-600" />
                  İş Hayatında Başarı Stratejileri
                </CardTitle>
                <CardDescription>
                  Profesyonel yaşamınız için özel öneriler
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {content.workplace_strategies.communication && (
                  <div className="p-4 bg-white rounded-lg">
                    <h4 className="font-semibold text-purple-800 mb-2">Etkili İletişim:</h4>
                    <ul className="space-y-1">
                      {content.workplace_strategies.communication.map((item, idx) => (
                        <li key={idx} className="text-slate-700 text-sm">• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {content.workplace_strategies.leadership && (
                  <div className="p-4 bg-white rounded-lg">
                    <h4 className="font-semibold text-purple-800 mb-2">Liderlik Becerileri:</h4>
                    <ul className="space-y-1">
                      {content.workplace_strategies.leadership.map((item, idx) => (
                        <li key={idx} className="text-slate-700 text-sm">• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {content.workplace_strategies.teamwork && (
                  <div className="p-4 bg-white rounded-lg">
                    <h4 className="font-semibold text-purple-800 mb-2">Ekip Çalışması:</h4>
                    <ul className="space-y-1">
                      {content.workplace_strategies.teamwork.map((item, idx) => (
                        <li key={idx} className="text-slate-700 text-sm">• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {content.workplace_strategies.conflict_management && (
                  <div className="p-4 bg-white rounded-lg">
                    <h4 className="font-semibold text-purple-800 mb-2">Çatışma Yönetimi:</h4>
                    <ul className="space-y-1">
                      {content.workplace_strategies.conflict_management.map((item, idx) => (
                        <li key={idx} className="text-slate-700 text-sm">• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Professional Development - Adult */}
          {content.professional_development && (
            <Card className="shadow-lg border-none bg-gradient-to-br from-sky-50 to-cyan-50" data-testid="prof-dev-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <TrendingUp className="w-6 h-6 text-cyan-600" />
                  Profesyonel Gelişim ve Kariyer Yönetimi
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {content.professional_development.career_advancement && (
                  <div>
                    <h4 className="font-semibold text-cyan-800 mb-2">Kariyer İlerleme:</h4>
                    <ul className="space-y-2">
                      {content.professional_development.career_advancement.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-1" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {content.professional_development.skill_development && (
                  <div>
                    <h4 className="font-semibold text-cyan-800 mb-2">Gelişim Alanları:</h4>
                    <div className="flex flex-wrap gap-2">
                      {content.professional_development.skill_development.map((skill, idx) => (
                        <span key={idx} className="px-3 py-1 bg-cyan-100 text-cyan-800 rounded-full text-sm">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {content.professional_development.networking && (
                  <div>
                    <h4 className="font-semibold text-cyan-800 mb-2">Networking:</h4>
                    <ul className="space-y-1">
                      {content.professional_development.networking.map((item, idx) => (
                        <li key={idx} className="text-slate-700">• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Work-Life Balance - Adult */}
          {content.work_life_balance && (
            <Card className="shadow-lg border-none bg-gradient-to-br from-rose-50 to-pink-50" data-testid="work-life-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Heart className="w-6 h-6 text-rose-600" />
                  İş-Yaşam Dengesi ve Kişisel Gelişim
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {content.work_life_balance.stress_management && (
                  <div>
                    <h4 className="font-semibold text-rose-800 mb-2">Stres Yönetimi:</h4>
                    <ul className="space-y-2">
                      {content.work_life_balance.stress_management.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-700">
                          <Lightbulb className="w-4 h-4 text-rose-600 flex-shrink-0 mt-1" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {content.work_life_balance.balance_tips && (
                  <div>
                    <h4 className="font-semibold text-rose-800 mb-2">Denge İpuçları:</h4>
                    <ul className="space-y-1">
                      {content.work_life_balance.balance_tips.map((item, idx) => (
                        <li key={idx} className="text-slate-700">• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {content.work_life_balance.burnout_prevention && (
                  <div className="p-3 bg-rose-100 border border-rose-300 rounded-lg">
                    <h4 className="font-semibold text-rose-900 mb-2">Tükenmişliği Önleme:</h4>
                    <ul className="space-y-1">
                      {content.work_life_balance.burnout_prevention.map((item, idx) => (
                        <li key={idx} className="text-rose-900 text-sm">• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Future Roadmap */}
          {content.future_roadmap && (
            <Card className="shadow-lg border-none bg-gradient-to-br from-green-50 to-emerald-50" data-testid="roadmap-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Target className="w-6 h-6 text-emerald-600" />
                  Gelecek İçin Yol Haritanız
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {content.future_roadmap.short_term && (
                  <div className="p-4 bg-white rounded-lg">
                    <h4 className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      Kısa Vadeli Hedefler (Bu Yıl)
                    </h4>
                    <ul className="space-y-1 ml-4">
                      {content.future_roadmap.short_term.map((goal, idx) => (
                        <li key={idx} className="text-slate-700">• {goal}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {content.future_roadmap.mid_term && (
                  <div className="p-4 bg-white rounded-lg">
                    <h4 className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      Orta Vadeli Hedefler (1-3 Yıl)
                    </h4>
                    <ul className="space-y-1 ml-4">
                      {content.future_roadmap.mid_term.map((goal, idx) => (
                        <li key={idx} className="text-slate-700">• {goal}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {content.future_roadmap.long_term && (
                  <div className="p-4 bg-white rounded-lg">
                    <h4 className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      Uzun Vadeli Vizyon (5+ Yıl)
                    </h4>
                    <ul className="space-y-1 ml-4">
                      {content.future_roadmap.long_term.map((vision, idx) => (
                        <li key={idx} className="text-slate-700">• {vision}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="p-3 bg-emerald-100 border border-emerald-300 rounded-lg">
                  <p className="text-sm text-emerald-900">
                    <strong>Unutma:</strong> Bu yol haritası bir rehberdir. Hedeflerin zamanla değişebilir ve bu normaldir. 
                    Önemli olan, kendini tanımaya devam etmen ve ilgi alanlarını keşfetmendir.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Generic content fallback */}
          {content.content && !content.strengths && (
            <Card className="shadow-lg border-none" data-testid="generic-content">
              <CardContent className="pt-6">
                <div className="prose prose-slate max-w-none">
                  <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{content.content}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sources Section */}
          <Card className="shadow-lg border-none bg-slate-50 mt-8">
            <CardHeader>
              <CardTitle className="text-lg">Kaynak ve Referanslar</CardTitle>
              <CardDescription>
                Bu rapor, aşağıdaki bilimsel kaynaklar ve uzmanların çalışmalarına dayanmaktadır
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <a href="https://www.amazon.com.tr/Enneagram-E%C5%9Fini-Ke%C5%9Ffet-%C4%B0smail-Acarkan/dp/6050845395" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 hover:bg-blue-50 p-2 rounded-lg transition-colors">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800 text-sm">İsmail Acarkan</h4>
                    <p className="text-xs text-slate-600">Enneagram Uzmanı</p>
                  </div>
                </a>

                <div className="flex items-start gap-3 p-2">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800 text-sm">Ziya Selçuk</h4>
                    <p className="text-xs text-slate-600">Eğitim Bilimci</p>
                  </div>
                </div>

                <a href="https://www.eduen.com.tr" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 hover:bg-emerald-50 p-2 rounded-lg transition-colors">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800 text-sm">Eduen.com.tr</h4>
                    <p className="text-xs text-slate-600">Eğitim Platformu</p>
                  </div>
                </a>

                <a href="https://www.enneagraminstitute.com" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 hover:bg-amber-50 p-2 rounded-lg transition-colors">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800 text-sm">The Enneagram Institute</h4>
                    <p className="text-xs text-slate-600">Uluslararası Otorite</p>
                  </div>
                </a>

                <a href="https://www.integrative9.com/enneagram/" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 hover:bg-rose-50 p-2 rounded-lg transition-colors">
                  <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 text-rose-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800 text-sm">Integrative9</h4>
                    <p className="text-xs text-slate-600">Araştırma Merkezi</p>
                  </div>
                </a>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-500 text-center">
                  Daha detaylı bilgi için{' '}
                  <button 
                    onClick={() => navigate('/about')}
                    className="text-blue-600 hover:text-blue-700 font-semibold underline"
                  >
                    Metodoloji sayfamızı
                  </button>
                  {' '}ziyaret edebilirsiniz.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}